import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FirebaseAuth } from "../../../../components/FirebaseAuth/firebase";

const normalizeError = (error, fallback) => {
  const text = String(error?.message || "").replace(/^Firebase:\s*/i, "").trim();
  return text || fallback;
};

const formatDate = (ts) => {
  if (!ts) return "—";
  if (typeof ts?.toDate === "function") {
    const dt = ts.toDate();
    if (dt instanceof Date && !Number.isNaN(dt.getTime())) return dt.toLocaleDateString();
  }
  if (ts instanceof Date && !Number.isNaN(ts.getTime())) {
    return ts.toLocaleDateString();
  }
  const sec = Number(ts?.seconds ?? ts?._seconds ?? 0);
  if (sec > 0) return new Date(sec * 1000).toLocaleDateString();
  const ms = Number(ts);
  if (Number.isFinite(ms) && ms > 0) return new Date(ms).toLocaleDateString();
  const parsed = Date.parse(String(ts));
  if (!Number.isNaN(parsed)) return new Date(parsed).toLocaleDateString();
  return "—";
};

const statusLabel = (status) =>
  String(status || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
const interviewLabel = (status) => String(status || "").toLowerCase() === "completed" ? "Interviewed" : statusLabel(status);
const formatDateTime = (value) => {
  if (!value) return "—";
  const date = typeof value?.toDate === "function"
    ? value.toDate()
    : new Date(Number(value?.seconds || value?._seconds || 0) * 1000 || value);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : "—";
};

const decisionChipColor = (decision) => {
  const value = String(decision || "pending").toLowerCase();
  if (value === "accepted") return "success";
  if (value === "declined") return "error";
  return "default";
};

const defaultSlot = () => ({ startsAt: "" });
const TIMEZONES = typeof Intl.supportedValuesOf === "function"
  ? Intl.supportedValuesOf("timeZone")
  : ["UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"];

const toDateTimeInputValue = (value) => {
  if (!value) return "";
  const date = typeof value?.toDate === "function"
    ? value.toDate()
    : new Date(value?.seconds ? value.seconds * 1000 : value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const normalizeCandidate = (candidate = null) => {
  if (!candidate || typeof candidate !== "object") return null;
  return {
    ...candidate,
    contractorDecision: candidate.contractor_decision || "pending",
    contractorDecisionAt: candidate.contractor_decision_at || null,
    interviewStatus: candidate.interview_status || "not_scheduled",
    interviewAt: candidate.interview_at || null,
    contractorFeedback: candidate.contractor_feedback || "",
    timeSlots: Array.isArray(candidate.time_slots)
      ? candidate.time_slots.map((slot) => ({
          ...slot,
          startsAt: slot.starts_at || null,
          selected: Boolean(slot.selected),
        }))
      : [],
  };
};

const HireInvitations = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("accept");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  const [timeSlots, setTimeSlots] = useState([defaultSlot(), defaultSlot(), defaultSlot()]);
  const [availabilityMessage, setAvailabilityMessage] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const loadInvitations = async () => {
    setLoading(true);
    setError("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable("getContractorHireInvitations");
      const res = await fn({});
      const data = Array.isArray(res?.data?.contracts) ? res.data.contracts : [];
      setRows(data.map((row) => ({
        ...row,
        clientUid: row.client_uid || "",
        clientName: row.client_name || "",
        companyName: row.company_name || "",
        roleTitle: row.role_title || "",
        roleLabel: row.role_label || "",
        pricingModel: row.pricing_model || "",
        hourlyBudgetCents: row.hourly_budget_cents || 0,
        monthlyBudgetCents: row.monthly_budget_cents || 0,
        createdAt: row.created_at || null,
        updatedAt: row.updated_at || null,
        candidate: normalizeCandidate(row.candidate),
      })));
    } catch (e) {
      setRows([]);
      setError(normalizeError(e, "Unable to load hire invitations."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvitations();
    const refreshOnFocus = () => loadInvitations();
    window.addEventListener("focus", refreshOnFocus);
    return () => window.removeEventListener("focus", refreshOnFocus);
  }, []);

  const actionableRows = useMemo(
    () => rows.filter((r) => ["submitted", "shortlisted"].includes(String(r.status || "").toLowerCase())),
    [rows]
  );

  const pagedRows = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const getClientLabel = (row) => {
    const name = String(row?.clientName || "").trim();
    if (name) return name;
    const company = String(row?.companyName || "").trim();
    if (company && company.toLowerCase() !== "client") return company;
    const uid = String(row?.clientUid || "").trim();
    if (uid) return `Client ${uid.slice(0, 6)}`;
    return "—";
  };

  const buildSlotsFromCandidate = (candidate) => {
    const existing = Array.isArray(candidate?.timeSlots) ? candidate.timeSlots : [];
    const slots = existing.slice(0, 3).map((slot) => ({
      startsAt: toDateTimeInputValue(slot.startsAt || slot.starts_at),
    }));
    while (slots.length < 3) {
      slots.push(defaultSlot());
    }
    return slots;
  };

  const openAcceptDialog = (row) => {
    setDialogMode("accept");
    setSelectedRequest(row);
    setTimezone(row?.candidate?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
    setTimeSlots(buildSlotsFromCandidate(row?.candidate));
    setAvailabilityMessage("");
    setDialogOpen(true);
  };

  const openAvailabilityDialog = (row) => {
    setDialogMode("availability");
    setSelectedRequest(row);
    setTimezone(row?.candidate?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
    setTimeSlots(buildSlotsFromCandidate(row?.candidate));
    setAvailabilityMessage(row?.candidate?.contractorFeedback || "");
    setDialogOpen(true);
  };

  const respond = async (row, decision, payload = {}) => {
    setSubmitting(true);
    setError("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable("contractorRespondHireInvitation");
      await fn({
        contract_id: row.id,
        decision,
        ...payload,
      });
      setDialogOpen(false);
      setSelectedRequest(null);
      await loadInvitations();
    } catch (e) {
      setError(normalizeError(e, "Unable to update invitation response."));
    } finally {
      setSubmitting(false);
    }
  };

  const submitAccept = async () => {
    if (!selectedRequest) return;
    const slots = timeSlots.map((slot, idx) => ({
      label: `Availability ${idx + 1}`,
      starts_at: slot.startsAt ? new Date(slot.startsAt).toISOString() : "",
    }));
    const invalid = !timezone || slots.some((slot) => !slot.starts_at);
    if (invalid) {
      setError("Please provide timezone and all 3 interview time slots.");
      return;
    }
    await respond(selectedRequest, "accepted", { timezone, time_slots: slots });
  };

  const submitAvailability = async () => {
    if (!selectedRequest) return;
    const slots = timeSlots.map((slot, idx) => ({
      label: `Availability ${idx + 1}`,
      starts_at: slot.startsAt ? new Date(slot.startsAt).toISOString() : "",
    }));
    const invalid = !timezone || slots.some((slot) => !slot.starts_at);
    if (invalid) {
      setError("Please provide timezone and all 3 interview time slots.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable("contractorSendInterviewAvailability");
      await fn({
        contract_id: selectedRequest.id,
        timezone,
        time_slots: slots,
        message: availabilityMessage,
        expected_version: Number(selectedRequest.version || 0),
      });
      setDialogOpen(false);
      setSelectedRequest(null);
      setAvailabilityMessage("");
      await loadInvitations();
    } catch (e) {
      setError(normalizeError(e, "Unable to send interview availability."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: embedded ? 0 : 2,
        borderRadius: embedded ? 0 : "8px",
        mb: embedded ? 0 : 2,
        border: embedded ? 0 : undefined,
        boxShadow: "none",
        bgcolor: "background.paper",
      }}
    >
      {!embedded && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Badge
              color="warning"
              badgeContent={actionableRows.length}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: "grey.700", color: "grey.200" }}>
                <CalendarMonthIcon fontSize="small" />
              </Avatar>
            </Badge>
            <Typography sx={{ fontWeight: 600 }}>Hire Invitations</Typography>
          </Stack>
          <Button size="small" startIcon={<RefreshIcon />} onClick={loadInvitations} disabled={loading} sx={{ textTransform: "none" }}>
            Refresh
          </Button>
        </Stack>
      )}

      {!!error && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Stack spacing={1} sx={{ p: embedded ? 2 : 0 }}>
          {Array.from({ length: 4 }).map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={36} />
          ))}
        </Stack>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Decision</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 4, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">No invitations yet.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row) => {
                  const decision = String(row?.candidate?.contractorDecision || "pending");
                  const interviewStatus = String(row?.candidate?.interviewStatus || "not_scheduled").toLowerCase();
                  const isPendingDecision = decision.toLowerCase() === "pending";
                  const canRespond =
                    isPendingDecision &&
                    ["submitted", "shortlisted"].includes(String(row.status || "").toLowerCase());
                  const canSendAvailability =
                    decision.toLowerCase() === "accepted" &&
                    ["requested", "not_scheduled"].includes(interviewStatus) &&
                    ["submitted", "shortlisted", "matched"].includes(String(row.status || "").toLowerCase());
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatDate(row.updatedAt || row.createdAt)}</TableCell>
                      <TableCell>{getClientLabel(row)}</TableCell>
                      <TableCell>{row.roleTitle || row.roleLabel || "—"}</TableCell>
                      <TableCell>
                        <Chip size="small" label={statusLabel(row.status)} sx={{ borderRadius: 1 }} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={decisionChipColor(decision)}
                          label={statusLabel(decision)}
                          sx={{ borderRadius: 1 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {canRespond ? (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Accept invitation and add interview slots">
                              <span>
                                <IconButton size="small" onClick={() => openAcceptDialog(row)} disabled={submitting}>
                                  <CheckOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Decline invitation">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => respond(row, "declined")}
                                  disabled={submitting}
                                >
                                  <CloseOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        ) : canSendAvailability ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CalendarMonthIcon fontSize="small" />}
                            onClick={() => openAvailabilityDialog(row)}
                            disabled={submitting}
                            sx={{ textTransform: "none" }}
                          >
                            Send availability
                          </Button>
                        ) : ["availability_sent", "scheduled", "completed", "no_show"].includes(interviewStatus) ? (
                          <Stack spacing={0.25} alignItems="flex-end">
                            <Chip
                              size="small"
                              color={interviewStatus === "scheduled" || interviewStatus === "completed" ? "success" : interviewStatus === "no_show" ? "error" : "warning"}
                              label={interviewLabel(interviewStatus)}
                              sx={{ borderRadius: 1 }}
                            />
                            {interviewStatus === "scheduled" && (
                              <Typography variant="caption" color="text.secondary">
                                {formatDateTime(row?.candidate?.interviewAt)}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={rows.length}
                  page={page}
                  onPageChange={(_event, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 20]}
                  labelRowsPerPage="Show"
                  colSpan={6}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2.5,
            pr: 7,
            fontWeight: 800,
          }}
        >
          {dialogMode === "accept" ? "Accept Invitation" : "Send Availability"}
          <IconButton
            size="medium"
            onClick={() => setDialogOpen(false)}
            sx={{ position: "absolute", right: 20, top: 18 }}
            aria-label="Close accept invitation dialog"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 2.5, sm: 3 }, py: 3 }}>
          <Stack spacing={2}>
            <Typography variant="body1" color="text.secondary">
              {dialogMode === "accept"
                ? "Accept the request and share 3 available interview slots."
                : "Share 3 available interview slots with the client."}
            </Typography>
            <TextField
              select
              label="Timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              fullWidth
            >
              {TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz}>{tz}</MenuItem>
              ))}
            </TextField>
            <Stack spacing={1.25}>
              {timeSlots.map((slot, idx) => (
                <Paper
                  key={`slot-${idx}`}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "background.paper",
                    borderColor: slot.startsAt ? "primary.light" : "divider",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: "rgba(25, 118, 210, 0.08)",
                        color: "primary.main",
                      }}
                    >
                      <CalendarMonthIcon />
                    </Avatar>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 800,
                        minWidth: { sm: 96 },
                      }}
                    >
                      Slot {idx + 1}
                    </Typography>
                    <TextField
                      type="datetime-local"
                      label="Availability"
                      InputLabelProps={{ shrink: true }}
                      value={slot.startsAt}
                      onChange={(e) =>
                        setTimeSlots((prev) => prev.map((row, i) => (i === idx ? { ...row, startsAt: e.target.value } : row)))
                      }
                      fullWidth
                    />
                  </Stack>
                </Paper>
              ))}
            </Stack>
            {dialogMode === "availability" && (
              <TextField
                size="small"
                label="Message to client"
                multiline
                minRows={3}
                value={availabilityMessage}
                onChange={(e) => setAvailabilityMessage(e.target.value.slice(0, 500))}
                helperText={`${availabilityMessage.length}/500 characters`}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<CalendarMonthIcon />}
            onClick={dialogMode === "accept" ? submitAccept : submitAvailability}
            disabled={submitting}
            size="large"
            sx={{
              ml: "auto",
              fontWeight: 800,
              textTransform: "uppercase",
            }}
          >
            {dialogMode === "accept" ? "Accept request" : "Send availability"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default HireInvitations;
